import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useToast } from "@/hooks/use-toast";
import { createAdminRoomListingAction } from "../actions/listings.actions";
import { ADMIN_LISTINGS_QUERY_KEYS } from "../queries/useAdminListings";
import { ListingType } from "@/components/modules/listings/api/schema";
const PROPERTY_TYPE_KIND_KEY: Record<string, string> = {
  room: "kindRoom"
};

/**
 * Admin: create a listing on behalf of a user. Mirrors
 * `useNewRoomListingMutation` (same success/error toast strings) but
 * targets the admin endpoint and invalidates the admin listings cache.
 */
export const useAdminCreateListing = () => {
  const $ = _c(10);
  const {
    toast
  } = useToast();
  const t = useTranslations("modules.listings.addRoomListing");
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== t || $[2] !== toast) {
    t0 = (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_LISTINGS_QUERY_KEYS.all
      });
      const submitted = variables.data;
      const isDemand = submitted.listingType === ListingType.DEMAND;
      const kindKey = PROPERTY_TYPE_KIND_KEY[submitted.propertyType ?? ""] ?? "kindRoom";
      const kind = t(kindKey);
      toast({
        title: isDemand ? t("demandCreatedTitle") : t("listingCreatedTitle"),
        description: isDemand ? t("demandCreatedDescription", {
          title: submitted.title ?? "",
          kind
        }) : t("listingCreatedDescription", {
          title: submitted.title ?? "",
          kind
        })
      });
    };
    $[0] = queryClient;
    $[1] = t;
    $[2] = toast;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== t || $[5] !== toast) {
    t1 = () => {
      toast({
        title: t("errorTitle"),
        description: t("errorDescription"),
        variant: "destructive"
      });
    };
    $[4] = t;
    $[5] = toast;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: createAdminRoomListingAction,
      onSuccess: t0,
      onError: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};